"use client";

import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const FAQ_ITEMS: FaqItem[] = [{
  id: "item-1",
  question: "How do you address moisture and waterproofing in older Decatur basements?",
  answer: "Many historic and mid-century homes in Decatur sit on porous brick or unreinforced cinder block foundations. Before any framing or drywall begins, we assess exterior grading, downspout discharge, and foundation wall integrity. We apply interior vapor barriers, sub-slab drainage systems, and high-performance sump pumps where needed to ensure the space remains completely dry year-round."
}, {
  id: "item-2",
  question: "What are the egress window requirements for basement bedrooms and living suites?",
  answer: "Under City of Decatur and Georgia residential building codes, any habitable basement bedroom must feature at least one code-compliant egress window or exterior doorway. Egress windows require a minimum net clear opening of 5.7 square feet, a minimum opening height of 24 inches, and a sill height no higher than 44 inches from the finished floor."
}, {
  id: "item-3",
  question: "Do basement remodeling projects require building and trade permits in Decatur?",
  answer: "Yes. Any renovation involving new partition walls, electrical branch circuits, plumbing additions, HVAC re-routing, or structural alterations requires a residential building permit from the City of Decatur or DeKalb County building departments. All work is inspected across rough-in and final completion milestones."
}, {
  id: "item-4",
  question: "How do you manage low ceiling heights, support beams, and exposed ductwork?",
  answer: "Older Decatur basements often have low ceiling clearance and low-hanging HVAC trunk lines. We strategically box in structural steel or timber beams with custom architectural soffits, re-engineer duct runs into compact high-velocity or low-profile designs, and utilize recessed LED lighting to maximize overall headroom."
}, {
  id: "item-5",
  question: "Can an unfinished basement be converted into an accessory living unit or guest suite?",
  answer: "Yes. Basement conversions can include full kitchens, private bathrooms, laundry hookups, and dedicated exterior access. We tie into your home's main water supply and sewer lateral—incorporating sewage ejector pump pits when gravity lines are above the slab grade—while adhering to local zoning and accessory dwelling standards."
}];
export default function BasementRemodelingFaq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="basementremodelingfaq" data-nav="light" ref={ref} className="bg-background text-foreground py-16 sm:py-20 lg:py-24 transition-opacity duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
            Basement Remodeling Questions
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans max-w-2xl mx-auto">
            Practical answers regarding foundation conditions, moisture mitigation, building permits, and egress codes for Decatur homes.
          </p>
        </div>

        <div className="bg-card/70 backdrop-blur-xs border border-border/80 rounded-xl shadow-sm p-4 sm:p-8">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-2">
            {FAQ_ITEMS.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="border border-border/60 rounded-lg px-4 sm:px-6 py-1 bg-background/50 data-[state=open]:bg-secondary/40 transition-colors">
                <AccordionTrigger className="font-serif text-base sm:text-lg text-left text-foreground hover:no-underline hover:text-chart-1 py-4">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed pt-1 pb-4">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}